import {
  AEMErrorMessage,
  CommonConstants,
  checkErrors,
  getEmptyContent,
  getImage,
  getLink,
  getLinkWithId,
  isDisplay,
  isError,
  isValidData
} from '@com-delta-idp/mnx-skymiles-shared';
import { Link } from '@com-delta-idp/mnx-ui-kit';
import { logError } from '@com-delta-idp/mnx-utils';
import {
  AEMpartner,
  PartnersPersonalizationEngineResponse
} from '../models/partners-personalization-engine.interfaces';
import {
  EnrolledPromotion,
  Linked,
  PartnersPromotionsResponse
} from '../models/partners-promotions.interfaces';
import { Params } from '../models/partners.constants';
import {
  getDate,
  getEnrollPromotions,
  getPartnerAriaLabels
} from './partners.helper';
import { Partner, Partners } from '@com-delta-omnichannel/aura';

export const getPartners = (
  promotion: PartnersPromotionsResponse | undefined,
  pe: PartnersPersonalizationEngineResponse | undefined,
  featureFlag: boolean | undefined,
  errors: (string | undefined)[],
  aemErrorMessages: AEMErrorMessage[] | undefined
): Partners => {
  try {
    if (checkErrors(errors, aemErrorMessages)) {
      return isError<Partners>(
        'partners',
        aemErrorMessages as AEMErrorMessage[],
        featureFlag
      );
    }
    return isValidData<
      string,
      [PartnersPersonalizationEngineResponse | undefined, boolean | undefined]
    >('contentDetails', pe, featureFlag)
      ? adaptPartners(
          featureFlag as boolean,
          promotion as PartnersPromotionsResponse,
          pe as PartnersPersonalizationEngineResponse
        )
      : isDisplay<Partners>(featureFlag);
  } catch (error) {
    logError(error as Error, 'SKYMILES', 'PARTNERS', 'PARTNERS');
    return getEmptyContent<Partners>('partners', aemErrorMessages, true);
  }
};

const adaptPartners = (
  featureFlag: boolean,
  promotion: PartnersPromotionsResponse | undefined,
  pe: PartnersPersonalizationEngineResponse
): Partners => {
  if (hasValidPartners(featureFlag, promotion, pe)) {
    return {
      title: pe.contentDetails.parsys.partners.sectionTitle,
      action: getLinkWithId(
        pe.contentDetails.parsys.partners.viewAllPartnersLabel,
        pe.contentDetails.parsys.partners.viewAllPartnersCTA,
        pe.contentDetails.parsys.partners.viewAllPartnersId
      ),
      items: getPartnersList(
        pe.contentDetails.parsys.partners.partners,
        promotion ? getEnrollPromotions(promotion) : undefined
      ),
      status: CommonConstants.EXIST,
      disabled: !featureFlag,
      loaded: true
    };
  } else {
    return getEmptyContent<Partners>('', [], true, true);
  }
};

const hasValidPartners = (
  featureFlag: boolean,
  promotion: PartnersPromotionsResponse | undefined,
  pe: PartnersPersonalizationEngineResponse
) => {
  return pe && Object.keys(pe).length && hasPromotion(promotion) && featureFlag;
};

const hasPromotion = (promotion: PartnersPromotionsResponse | undefined) => {
  return (promotion && Object.keys(promotion).length) || !promotion;
};

export const getPartnersList = (
  rawPartners: string[],
  promotion: EnrolledPromotion[] | undefined
): Partner[] => {
  const partners: Partner[] = [];
  rawPartners.forEach((rawPartner: string) => {
    const aemPartner: AEMpartner = JSON.parse(rawPartner);
    partners.push(getPartner(aemPartner, promotion));
  });
  return partners;
};

export const getPartner = (
  aemPartner: AEMpartner,
  promotion: EnrolledPromotion[] | undefined
): Partner => {
  const partner: Partner = {
    id: aemPartner.id,
    logo: getImage(aemPartner.logo, aemPartner.logoAltText),
    title: aemPartner.title,
    action: enrolledPartner(aemPartner, promotion)
  };
  if (aemPartner.ctaAriaLabel !== '' && aemPartner.ctaAriaLabel) {
    return { ...partner, ariaLabels: getPartnerAriaLabels(aemPartner) };
  } else {
    return partner;
  }
};

export const enrolledPartner = (
  aemPartner: AEMpartner,
  promotion: EnrolledPromotion[] | undefined
): Link => {
  const linked: Linked = {
    status: false,
    date: ''
  };
  promotion?.forEach((enrolledPromotion) => {
    if (enrolledPromotion.promotionCode.includes(aemPartner.promoCode)) {
      linked.status = true;
      linked.date = enrolledPromotion.enrollmentStartDate;
    }
  });
  if (linked.status && promotion) {
    const linkLabel = Params.LINKED + ' ' + getDate(linked.date);
    return getLink(linkLabel, '');
  } else {
    return getLink(aemPartner.ctaLabel, aemPartner.ctaURL);
  }
};
